import React, { useRef, useState } from 'react';
import s from './Profile.module.css';
import { useSelector } from 'react-redux';
import { selectIsUpdating, selectUser } from '../../../../store/selectors/authSelectors';
import IconProfilePencil from '../../../../shared/ui/icons/IconProfilePencil';
import AvatarComponent from '../../../../shared/ui/avatar/AvatarComponent';
import CropAvatarModal from './CropAvatarModal';
import { useAppDispatch } from '../../../../store/store';
import { removeProfilePhoto, uploadProfilePhoto } from '../../../../shared/api/authApi';

type EditMode = 'existing' | 'new' | null;

const AvatarSection: React.FC = () => {
    const user = useSelector(selectUser);
    const isUpdating = useSelector(selectIsUpdating);
    const dispatch = useAppDispatch();
    const fileRef = useRef<HTMLInputElement | null>(null);

    const [showCropper, setShowCropper] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [mode, setMode] = useState<EditMode>(null);

    const openFile = () => fileRef.current?.click();

    const loadExistingAsObjectURL = async (url: string) => {
        try {
            const res = await fetch(url, { mode: 'cors' });
            const blob = await res.blob();
            return URL.createObjectURL(blob);
        } catch {
            return url;
        }
    };

    const onEditClick = async () => {
        if (!user) return;
        if (user.profile_picture) {
            const localUrl = await loadExistingAsObjectURL(user.profile_picture);
            setImageSrc(localUrl);
            setMode('existing');
            setShowCropper(true);
        } else {
            openFile();
        }
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        setImageSrc(url);
        setMode('new');
        setShowCropper(true);
        e.target.value = '';
    };

    const handleSave = async (blob: Blob) => {
        if (!user) return;
        try {
            setShowCropper(false);
            await dispatch(uploadProfilePhoto({ id: String(user.id), file: blob })).unwrap();
            if (imageSrc?.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
            setImageSrc(null);
            setMode(null);
        } catch {}
    };

    const handleCancel = () => {
        if (isUpdating) return;
        setShowCropper(false);
        if (imageSrc?.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
        setImageSrc(null);
        setMode(null);
    };

    const handleConfirmDeleteExisting = async () => {
        if (!user) return;
        try {
            setShowCropper(false);
            await dispatch(removeProfilePhoto({ id: String(user.id) })).unwrap();
            if (imageSrc?.startsWith('blob:')) URL.revokeObjectURL(imageSrc);
            setImageSrc(null);
            setMode(null);
        } catch {}
    };

    return (
        <div className={s.avatarSection}>
            <div className={s.avatarWrap}>
                <AvatarComponent
                    avatarUrl={user?.profile_picture ?? ''}
                    userName={user?.username || user?.email || ''}
                    width={120}
                    height={120}
                />
                {isUpdating && (
                    <div className={s.avatarLoading}>
                        <div className={s.spinner} />
                    </div>
                )}
            </div>

            <button
                className={s.editAvatar}
                onClick={onEditClick}
                aria-label="Edit avatar"
                disabled={isUpdating}
            >
                <IconProfilePencil />
            </button>

            <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onFileChange}
            />

            {showCropper && imageSrc && (
                <CropAvatarModal
                    src={imageSrc}
                    isEditingExisting={mode === 'existing'}
                    busyExternal={isUpdating}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    onChangeImage={openFile}
                    onConfirmDeleteExisting={handleConfirmDeleteExisting}
                />
            )}
        </div>
    );
};

export default AvatarSection;
